/**
 * StyleProfilePanel — the visible, correctable output of the archive-extraction
 * pass. Shows what the one-time analysis measured from our OWN photos (palette
 * + image-kind mix) with per-field confidence + sample size, lets the operator
 * correct misreads, and is re-runnable. Honest by design: nothing is a black box.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, RefreshCw, Info, Save, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

const CONF_STYLE = {
  high: "bg-emerald-100 text-emerald-800 border-emerald-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-rose-100 text-rose-800 border-rose-300",
};

function ConfidenceBadge({ field }) {
  if (!field) return null;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border ${CONF_STYLE[field.confidence] || "bg-slate-100 text-slate-600 border-slate-300"}`}>
      {field.confidence} · n={field.sample_size}
    </span>
  );
}

export default function StyleProfilePanel({ userIsAdmin }) {
  const [wsId, setWsId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [effective, setEffective] = useState({});
  const [diff, setDiff] = useState(null);
  const [mixDraft, setMixDraft] = useState(null);

  const load = useCallback(async (ws) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${ws}/style-profile`, { credentials: "include" });
      const d = await r.json();
      setProfile(d.profile);
      setEffective(d.effective || {});
    } catch (e) {
      toast.error(`Could not load style profile: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/v2/workspaces/me`, { credentials: "include" });
        const list = await r.json();
        const ws = Array.isArray(list) && list.length ? list[0].workspace_id : null;
        setWsId(ws);
        if (ws) await load(ws);
        else setLoading(false);
      } catch {
        setLoading(false);
      }
    })();
  }, [load]);

  const runExtract = async () => {
    if (!wsId) return;
    setExtracting(true);
    setDiff(null);
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/style-profile/extract`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setProfile(d.profile);
      setEffective(d.effective || {});
      setDiff(d.diff || null);
      setMixDraft(null);
      toast.success(`Analysed ${d.profile?.corpus?.analysed} photos`);
    } catch (e) {
      toast.error(`Extraction failed: ${e.message}`);
    } finally {
      setExtracting(false);
    }
  };

  const saveMix = async () => {
    if (!mixDraft) return;
    const total = (mixDraft.people || 0) + (mixDraft.scene || 0) + (mixDraft.abstract || 0);
    if (Math.abs(total - 1) > 0.02) {
      toast.error(`Mix must sum to ~100% (currently ${Math.round(total * 100)}%)`);
      return;
    }
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/style-profile`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field: "image_kind_mix", value: mixDraft }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setEffective(d.effective || {});
      setMixDraft(null);
      toast.success("Correction saved");
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  };

  const resetMix = async () => {
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/style-profile`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field: "image_kind_mix", value: null }),
      });
      const d = await r.json();
      setEffective(d.effective || {});
      setMixDraft(null);
      toast.success("Reset to extracted value");
    } catch (e) {
      toast.error(`Reset failed: ${e.message}`);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading style profile…</div>;
  }

  const palette = effective?.palette?.value || [];
  const paletteField = profile?.extracted?.palette;
  const mixField = effective?.image_kind_mix;
  const mix = mixDraft || mixField?.value || {};
  const corpus = profile?.corpus;

  return (
    <div className="space-y-5" data-testid="style-profile-panel">
      {/* Honest framing */}
      <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-4 text-sm text-slate-700 space-y-1.5">
        <div className="flex items-center gap-2 font-semibold text-slate-900">
          <Info className="h-4 w-4 text-sky-600" /> What this is — and isn't
        </div>
        <p className="text-[13px] leading-relaxed">
          A one-time, re-runnable analysis of your own photos. It extracts measurable patterns into editable
          defaults — it does <strong>not</strong> learn design judgment. It raises <strong>consistency</strong> on routine
          posts; it does not raise the creative ceiling for novel briefs. Verify every field below — a polished,
          on-brand look can mask a misread, so don't trust it blind.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Extracted style profile</h3>
          {corpus ? (
            <p className="text-xs text-slate-500">
              Analysed <strong>{corpus.analysed}</strong> of <strong>{corpus.available}</strong> own photos
              {corpus.available > corpus.capped_at ? ` (random sample, cap ${corpus.capped_at})` : ""} ·
              last run {profile?.extracted_at ? new Date(profile.extracted_at).toLocaleString() : "—"}
            </p>
          ) : (
            <p className="text-xs text-slate-500">No profile yet — run the extraction to begin.</p>
          )}
        </div>
        {userIsAdmin && (
          <Button onClick={runExtract} disabled={extracting} data-testid="style-profile-extract-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
            {extracting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            {profile ? "Re-run extraction" : "Run extraction"}
          </Button>
        )}
      </div>

      {extracting && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Sampling and analysing your photos (~15s)…</p>
      )}

      {!profile ? null : (
        <>
          {/* Palette */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="style-profile-palette">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-900">Palette — your actual imagery colours</h4>
              <ConfidenceBadge field={paletteField} />
            </div>
            <p className="text-xs text-slate-500">
              Only <strong>{Math.round((paletteField?.on_brand_pct || 0) * 100)}%</strong> of your imagery palette is
              close to a RESILIGENT brand colour — the rest is neutral/other. (This is honest signal, not a problem to
              hide: it's why the brand duotone matters for making photos read as on-brand.)
            </p>
            <div className="flex flex-wrap gap-2">
              {palette.map((c, i) => (
                <div key={i} className="rounded-md border border-slate-200 overflow-hidden w-28" data-testid={`palette-swatch-${i}`}>
                  <div className="h-12" style={{ backgroundColor: c.hex }} />
                  <div className="px-1.5 py-1 text-[10px] text-slate-600">
                    <div className="font-mono">{c.hex} · {Math.round(c.pct * 100)}%</div>
                    <div className="flex items-center justify-between">
                      <span>{c.nearest_brand}</span>
                      {c.on_brand && <span className="text-emerald-600 font-semibold">on-brand</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Image-kind mix (editable) */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="style-profile-mix">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-900">Image-kind mix — feeds the router</h4>
              <div className="flex items-center gap-2">
                {mixField?.corrected && <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">corrected</span>}
                <ConfidenceBadge field={mixField} />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Detected mechanically (faces → people; flat/low-detail → abstract; else scene). If the detector
              misread your style, correct it — the router uses your corrected value.
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {["people", "scene", "abstract"].map((k) => (
                <div key={k}>
                  <label className="text-xs text-slate-600 capitalize">{k}</label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" min="0" max="1" step="0.05"
                      value={mix[k] ?? 0}
                      disabled={!userIsAdmin}
                      onChange={(e) => setMixDraft({ ...mix, [k]: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-sm"
                      data-testid={`mix-input-${k}`}
                    />
                    <span className="text-xs text-slate-400">{Math.round((mix[k] ?? 0) * 100)}%</span>
                  </div>
                </div>
              ))}
            </div>
            {userIsAdmin && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={saveMix} disabled={!mixDraft} data-testid="mix-save-btn" className="bg-slate-900 text-white">
                  <Save className="h-3.5 w-3.5 mr-1" /> Save correction
                </Button>
                <Button size="sm" variant="outline" onClick={resetMix} disabled={!mixField?.corrected && !mixDraft} data-testid="mix-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset to extracted
                </Button>
              </div>
            )}
          </section>

          {diff?.image_kind_mix && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="style-profile-diff">
              <div className="flex items-center gap-1.5 font-semibold text-slate-800 mb-1"><ShieldAlert className="h-3.5 w-3.5" /> Change since last run</div>
              {Object.entries(diff.image_kind_mix).map(([k, v]) => (
                <span key={k} className="mr-3 capitalize">{k}: {v >= 0 ? "+" : ""}{Math.round(v * 100)}%</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
